// Filter engine for the blogger catalog — works with the API Blogger type.
import type { Blogger } from '@/shared/api/wb';

export type Platform = string;
export type SortKey =
  | 'relevance'
  | 'reach_desc' | 'reach_asc'
  | 'price_asc'  | 'price_desc'
  | 'cpv_asc'    | 'cpv_desc'
  | 'score_desc' | 'score_asc';

export type FilterState = {
  platform: string[];
  reachMin: number | null;
  reachMax: number | null;
  priceMin: number | null;
  priceMax: number | null;
  hidePriceNull: boolean;
  budgetFit: number | null;
  cpvMin: number | null;
  cpvMax: number | null;
  erMin: number | null;
  erMax: number | null;
  scoreMin: number | null;
  search: string;
  niche: string[];
  excludeNiche: string[];
};

export const DEFAULT_FILTERS: FilterState = {
  platform: [],
  reachMin: null, reachMax: null,
  priceMin: null, priceMax: null,
  hidePriceNull: false, budgetFit: null,
  cpvMin: null, cpvMax: null,
  erMin: null, erMax: null,
  scoreMin: null,
  search: '',
  niche: [], excludeNiche: [],
};

export const PLATFORMS_META: { id: string; label: string }[] = [
  { id: 'telegram', label: 'Telegram' },
  { id: 'vk', label: 'VKontakte' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'dzen', label: 'Дзен' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'max', label: 'MAX' },
  { id: 'rutube', label: 'Rutube' },
];

// Blogger-niche codes → Russian display labels. Mirrors the 44-category taxonomy
// from the production wowblogger.ru catalog (/bloggers filters) — same ids and
// order as backend/app/seed_data.py::NICHE_LABEL.
export const CAT_LABELS: Record<string, string> = {
  auto: 'Автомобили',
  author_blog: 'Авторский блог',
  biz: 'Бизнес',
  family: 'Дети, семья и отношения',
  design_photo: 'Дизайн и фотография',
  interior: 'Дизайн интерьеров, дом и сад',
  food: 'Еда и кафе',
  animals: 'Животные',
  expat: 'Жизнь за границей',
  health: 'Здоровье и медицина',
  games: 'Игры',
  invest: 'Инвестиции',
  art: 'Искусство и культура',
  history: 'Историческое и познавательное',
  books: 'Книги и чтение',
  beauty: 'Красота и косметика',
  esoteric: 'Магия и эзотерика',
  marketing: 'Маркетинг',
  fashion: 'Мода и шопинг',
  motivation: 'Мотивация и саморазвитие',
  music: 'Музыка и танцы',
  science: 'Наука и технологии',
  realty: 'Недвижимость',
  news: 'Новости и СМИ',
  lifestyle: 'Образ жизни',
  edu: 'Образование',
  politics: 'Политика',
  psychology: 'Психология',
  jobs: 'Работа и вакансии',
  regional: 'Региональные',
  religion: 'Религия',
  diy: 'Рукоделие и полезные советы',
  deals: 'Скидки и акции',
  sport: 'Спорт',
  construction: 'Строительство и ремонт',
  it: 'Сфера ИТ',
  cinema: 'Театр, кино и аниме',
  travel: 'Туризм и путешествия',
  fitness: 'Фитнес, тренировки и медитация',
  hobby: 'Хобби, отдых и развлечения',
  finance: 'Экономика и финансы',
  humor: 'Юмор и мемы',
  law: 'Юриспруденция',
  adult: '18+',
};

// Canonical display order (same as the production filter list) — used instead
// of alphabetical sort so niche dropdowns read the way advertisers expect.
const NICHE_ORDER = Object.keys(CAT_LABELS);

// Subtopics per category — reference data only (shown as a hint in filter UI),
// not tagged on individual bloggers. Mirrors backend/app/seed_data.py::CAT_SUBTOPICS.
export const CAT_SUBTOPICS: Record<string, string[]> = {
  auto: ['Легковые авто', 'Мототехника', 'Грузовой и спецтранспорт', 'Автосервис и автотовары', 'Водный и другой транспорт'],
  biz: ['Предпринимательство', 'Стартапы и венчур', 'B2B-услуги и производство'],
  family: ['Беременность и роды', 'Товары для детей', 'Отношения'],
  design_photo: ['Графический и UX-дизайн', 'Фотография', 'Иллюстрация и арт'],
  interior: ['Дизайн интерьера', 'Дача, сад и огород', 'Товары для дома и бытовая техника'],
  food: ['Рестораны и кафе', 'Рецепты и кулинария', 'Продукты питания'],
  animals: ['Домашние питомцы', 'Товары и услуги для животных'],
  health: ['Медицинские услуги', 'Фармацевтика и БАДы'],
  games: ['Видеоигры', 'Киберспорт', 'Настольные игры'],
  cinema: ['Кино и сериалы', 'Аниме', 'Театр'],
  books: ['Художественная литература', 'Нонфикшн'],
  beauty: ['Уход за собой', 'Косметика и парфюмерия', 'Салоны и мастера'],
  marketing: ['Digital и перформанс', 'SMM и контент-маркетинг', 'PR и коммуникации'],
  fashion: ['Одежда и обувь', 'Аксессуары'],
  science: ['Популярная наука', 'Инженерия и производство'],
  realty: ['Жилая', 'Загородная', 'Коммерческая'],
  psychology: ['Личная психология', 'Семейная психология'],
  diy: ['DIY и рукоделие', 'Коллекционирование'],
  travel: ['Внутренний туризм', 'Зарубежные направления'],
  fitness: ['Тренировки и фитнес', 'Медитация и йога'],
  hobby: ['Досуг и развлечения', 'Конкурсы и мероприятия'],
  expat: ['Релокация', 'Экспат-жизнь'],
  construction: ['Стройматериалы', 'Ремонтные услуги'],
  law: ['Частное право', 'Бизнес-право'],
  edu: ['Курсы и онлайн-обучение', 'Языки и лингвистика', 'Школа и студенчество'],
  motivation: ['Личностный рост', 'Продуктивность'],
  jobs: ['Вакансии и поиск работы', 'HR и рекрутинг', 'Развитие карьеры'],
  news: ['Федеральные новости', 'Общественно-политическое'],
  deals: ['Промокоды и распродажи', 'Маркетплейсы'],
  it: ['Софт и приложения', 'Разработка и devtools', 'Гаджеты и электроника'],
  finance: ['Личные финансы', 'Банки и страхование', 'Макроэкономика'],
  invest: ['Фондовый рынок', 'Недвижимость как актив'],
};

export function catLabel(c: string): string {
  return CAT_LABELS[c] ?? c;
}

export function catSubtopics(c: string): string[] {
  return CAT_SUBTOPICS[c] ?? [];
}

export function sortNiches(ids: string[]): string[] {
  return [...ids].sort((a, b) => NICHE_ORDER.indexOf(a) - NICHE_ORDER.indexOf(b));
}

export function applyFilters(bloggers: Blogger[], f: FilterState): Blogger[] {
  return bloggers.filter(b => {
    if (f.platform.length > 0 && !f.platform.includes(b.platform)) return false;

    if (f.reachMin !== null && b.reach < f.reachMin) return false;
    if (f.reachMax !== null && b.reach > f.reachMax) return false;

    if (!b.price) {
      if (f.hidePriceNull) return false;
      if (f.budgetFit !== null) return false;
    } else {
      if (f.priceMin !== null && b.price < f.priceMin) return false;
      if (f.priceMax !== null && b.price > f.priceMax) return false;
      if (f.budgetFit !== null && b.price > f.budgetFit) return false;
    }

    if (f.platform.length === 1) {
      if (f.cpvMin !== null && b.cpv < f.cpvMin) return false;
      if (f.cpvMax !== null && b.cpv > f.cpvMax) return false;
    }

    if (f.erMin !== null && b.er < f.erMin) return false;
    if (f.erMax !== null && b.er > f.erMax) return false;
    if (f.scoreMin !== null && b.score < f.scoreMin) return false;

    if (f.niche.length > 0 && !f.niche.some(n => b.cats.includes(n))) return false;
    if (f.excludeNiche.length > 0 && f.excludeNiche.some(n => b.cats.includes(n))) return false;

    if (f.search) {
      const q = f.search.toLowerCase();
      if (
        !b.name.toLowerCase().includes(q) &&
        !b.full.toLowerCase().includes(q) &&
        !b.cats.some(c => catLabel(c).toLowerCase().includes(q))
      ) return false;
    }

    return true;
  });
}

export function sortBloggers(bloggers: Blogger[], sort: SortKey): Blogger[] {
  const a = [...bloggers];
  if (sort === 'reach_desc')  return a.sort((x, y) => y.reach - x.reach);
  if (sort === 'reach_asc')   return a.sort((x, y) => x.reach - y.reach);
  if (sort === 'price_asc')   return a.sort((x, y) => (x.price ?? 0) - (y.price ?? 0));
  if (sort === 'price_desc')  return a.sort((x, y) => (y.price ?? 0) - (x.price ?? 0));
  if (sort === 'cpv_asc')     return a.sort((x, y) => x.cpv - y.cpv);
  if (sort === 'cpv_desc')    return a.sort((x, y) => y.cpv - x.cpv);
  if (sort === 'score_desc')  return a.sort((x, y) => y.score - x.score);
  if (sort === 'score_asc')   return a.sort((x, y) => x.score - y.score);
  return a.sort((x, y) => y.reach - x.reach); // relevance
}

export function facetCounts(
  all: Blogger[],
  filters: FilterState,
  key: 'platform' | 'niche',
  values: string[],
): Record<string, number> {
  const clearKey = key === 'platform' ? 'platform' : 'niche';
  const base = applyFilters(all, { ...filters, [clearKey]: [] });
  return Object.fromEntries(values.map(v => {
    const count = key === 'platform'
      ? base.filter(b => b.platform === v).length
      : base.filter(b => b.cats.includes(v)).length;
    return [v, count];
  }));
}

export function countActiveFilters(f: FilterState): number {
  let n = 0;
  if (f.platform.length) n++;
  if (f.reachMin !== null || f.reachMax !== null) n++;
  if (f.priceMin !== null || f.priceMax !== null) n++;
  if (f.hidePriceNull) n++;
  if (f.budgetFit !== null) n++;
  if (f.cpvMin !== null || f.cpvMax !== null) n++;
  if (f.erMin !== null || f.erMax !== null) n++;
  if (f.scoreMin !== null) n++;
  if (f.search) n++;
  if (f.niche.length) n++;
  if (f.excludeNiche.length) n++;
  return n;
}

export function serializeFilters(f: FilterState): URLSearchParams {
  const p = new URLSearchParams();
  if (f.platform.length) p.set('platform', f.platform.join(','));
  if (f.reachMin !== null) p.set('rmin', String(f.reachMin));
  if (f.reachMax !== null) p.set('rmax', String(f.reachMax));
  if (f.priceMin !== null) p.set('pmin', String(f.priceMin));
  if (f.priceMax !== null) p.set('pmax', String(f.priceMax));
  if (f.hidePriceNull) p.set('hideNull', '1');
  if (f.budgetFit !== null) p.set('budget', String(f.budgetFit));
  if (f.cpvMin !== null) p.set('cpvmin', String(f.cpvMin));
  if (f.cpvMax !== null) p.set('cpvmax', String(f.cpvMax));
  if (f.erMin !== null) p.set('ermin', String(f.erMin));
  if (f.erMax !== null) p.set('ermax', String(f.erMax));
  if (f.scoreMin !== null) p.set('score', String(f.scoreMin));
  if (f.search) p.set('q', f.search);
  if (f.niche.length) p.set('niche', f.niche.join(','));
  if (f.excludeNiche.length) p.set('xniche', f.excludeNiche.join(','));
  return p;
}

export function deserializeFilters(p: URLSearchParams): FilterState {
  const f = { ...DEFAULT_FILTERS };
  if (p.has('platform')) f.platform = p.get('platform')!.split(',');
  if (p.has('rmin')) f.reachMin = Number(p.get('rmin'));
  if (p.has('rmax')) f.reachMax = Number(p.get('rmax'));
  if (p.has('pmin')) f.priceMin = Number(p.get('pmin'));
  if (p.has('pmax')) f.priceMax = Number(p.get('pmax'));
  if (p.get('hideNull') === '1') f.hidePriceNull = true;
  if (p.has('budget')) f.budgetFit = Number(p.get('budget'));
  if (p.has('cpvmin')) f.cpvMin = Number(p.get('cpvmin'));
  if (p.has('cpvmax')) f.cpvMax = Number(p.get('cpvmax'));
  if (p.has('ermin')) f.erMin = Number(p.get('ermin'));
  if (p.has('ermax')) f.erMax = Number(p.get('ermax'));
  if (p.has('score')) f.scoreMin = Number(p.get('score'));
  if (p.has('q')) f.search = p.get('q')!;
  if (p.has('niche')) f.niche = p.get('niche')!.split(',');
  if (p.has('xniche')) f.excludeNiche = p.get('xniche')!.split(',');
  return f;
}
